import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";

// Zastępstwa pracowników (port modułu zastępstw z Magistratu).
// Zarządzanie w ustawieniach (ReplacementsTab), przełączanie konta z popupu konta
// w sidebarze. Przełączenie zwraca NOWY token z claimami Nota_SwitchedTo* — FE zapisuje
// go w UserManager i przeładowuje aplikację (patrz utils/replacementSwitch.ts).

export const replacementsQueryKeys = {
  list: "replacements.list",
  switches: "replacements.switches",
};

export interface IReplacement {
  Id: string;
  ReplacedUserId: string;
  ReplacedUserLabel?: string | null;
  ReplacingUserId: string;
  ReplacingUserLabel?: string | null;
  /** yyyy-MM-dd */
  StartDate: string;
  /** yyyy-MM-dd */
  EndDate: string;
  CreatedByUserId: string;
  CreatedByLabel?: string | null;
  CreationDate: string;
  IsActive: boolean;
  IsArchived: boolean;
  CanEdit: boolean;
}

export interface IReplacementsListResponse {
  Items: IReplacement[];
  CanManageOthers: boolean;
}

export interface ISaveReplacementRequest {
  Id?: string | null;
  /** null = za siebie */
  ReplacedUserId?: string | null;
  ReplacingUserId: string;
  StartDate: string;
  EndDate: string;
}

export interface IReplacementSwitch {
  ReplacementId: string;
  UserId: string;
  Label?: string | null;
  EndDate: string;
}

export interface IReplacementSwitchesResponse {
  Items: IReplacementSwitch[];
  SwitchedToUserId?: string | null;
  SwitchedToLabel?: string | null;
}

export interface ISwitchReplacementResponse {
  TargetUserId?: string | null;
  TargetLabel?: string | null;
  AccessToken: string;
  RefreshToken: string;
  ExpiresIn: number;
}

const getList = (
  archived: boolean,
  replacedUserId?: string | null,
): Promise<{ data: IReplacementsListResponse }> =>
  axiosInstance.get("replacements", {
    params: { archived, replacedUserId: replacedUserId ?? undefined },
  });

const useGetList = (archived: boolean, replacedUserId?: string | null) =>
  useCustomQuery([replacementsQueryKeys.list, archived, replacedUserId], () =>
    getList(archived, replacedUserId),
  );

const save = (
  payload: ISaveReplacementRequest,
): Promise<{ data: IReplacement }> =>
  axiosInstance.post("replacements", payload);

const useSave = () => useCustomMutation(save);

const remove = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`replacements/${id}`);

const useRemove = () => useCustomMutation(remove);

const getSwitches = (): Promise<{ data: IReplacementSwitchesResponse }> =>
  axiosInstance.get("replacements/switches");

const useGetSwitches = (enabled = true) =>
  useCustomQuery(replacementsQueryKeys.switches, () => getSwitches(), {
    enabled,
    staleTime: 60_000,
  });

const switchUser = (
  targetUserId: string | null,
): Promise<{ data: ISwitchReplacementResponse }> =>
  axiosInstance.post("replacements/switch", { TargetUserId: targetUserId });

const useSwitch = () => useCustomMutation(switchUser);

const replacementsService = {
  getList,
  useGetList,
  save,
  useSave,
  remove,
  useRemove,
  getSwitches,
  useGetSwitches,
  switchUser,
  useSwitch,
};

export default replacementsService;
